import { getApiBaseUrl } from "src/config/Endpoints";
import type { ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

const API_BASE_URL = getApiBaseUrl();

export type DashboardNarrativeKey =
  | "web_analytics"
  | "geo_spatial"
  | "sales_product_analytics"
  | "cltv_dashboard"
  | "organic_media"
  | "paid_media"
  | "engagement_overview"
  | "engagements_campaigns";

export interface DashboardNarrativePayload {
  dashboard_key: DashboardNarrativeKey;
  tab_key?: string;
  data: Record<string, unknown>;
  prompt_version: string;
  force_refresh: boolean;
  lang?: ApiLang;
}

export type DashboardNarrativeSegmentType = "text" | "positive" | "negative";

export interface DashboardNarrativeSegment {
  text: string;
  type: DashboardNarrativeSegmentType;
}

export type DashboardNarrativeSentiment = "positive" | "negative" | "neutral";

export interface DashboardNarrativeInsight {
  text: string;
  sentiment?: DashboardNarrativeSentiment;
}

export interface DashboardNarrativeResponse {
  dashboard_key: DashboardNarrativeKey;
  tab_key?: string | null;
  narrative: {
    summary?: string;
    summary_segments?: DashboardNarrativeSegment[];
    key_insights: Array<string | DashboardNarrativeInsight>;
    recommended_actions: string[];
  };
  narrative_cached: boolean;
  ai_summary_disabled?: boolean;
}

export interface DashboardAiSummaryUsage {
  used: number;
  limit: number;
  remaining: number;
}

export interface DashboardAiSummaryToggleResponse {
  dashboard_key: DashboardNarrativeKey;
  enabled: boolean;
  feature_available: boolean;
  usage?: DashboardAiSummaryUsage;
}

export async function fetchDashboardNarrative(
  payload: DashboardNarrativePayload,
): Promise<DashboardNarrativeResponse> {
  const query = payload.lang ? `?lang=${encodeURIComponent(payload.lang)}` : "";
  const response = await fetch(`${API_BASE_URL}/dashboards/narrative${query}`, {
    method: "POST",
    headers: buildAuthHeaders("api_key"),
    body: JSON.stringify(payload),
  });
  return handleJsonResponse<DashboardNarrativeResponse>(response);
}

export async function fetchDashboardAiSummaryToggle(
  dashboardKey: DashboardNarrativeKey,
): Promise<DashboardAiSummaryToggleResponse> {
  const response = await fetch(
    `${API_BASE_URL}/dashboards/narrative/ai-summary-toggle?dashboard_key=${encodeURIComponent(dashboardKey)}`,
    {
      method: "GET",
      headers: buildAuthHeaders("api_key"),
    },
  );
  return handleJsonResponse<DashboardAiSummaryToggleResponse>(response);
}

export async function updateDashboardAiSummaryToggle(
  dashboardKey: DashboardNarrativeKey,
  enabled: boolean,
): Promise<DashboardAiSummaryToggleResponse> {
  const response = await fetch(`${API_BASE_URL}/dashboards/narrative/ai-summary-toggle`, {
    method: "PUT",
    headers: buildAuthHeaders("api_key"),
    body: JSON.stringify({ dashboard_key: dashboardKey, enabled }),
  });
  return handleJsonResponse<DashboardAiSummaryToggleResponse>(response);
}
